
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCompany } from '@/context/CompanyContext';

export interface ActivityLog {
  id: string;
  user_id: string;
  action: string;
  entity_type: string;
  entity_name: string;
  details?: any;
  created_at: string;
  user?: {
    full_name: string | null;
    email: string;
  };
}

export const useActivityLog = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentCompany } = useCompany();

  const fetchLogs = async () => {
    try {
      if (!currentCompany?.id) {
        setLogs([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('activity_logs')
        .select(`
          *,
          profiles!activity_logs_user_id_fkey (
            full_name,
            email
          )
        `)
        .eq('company_id', currentCompany.id)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const formattedLogs = data?.map(log => ({
        ...log,
        user: (log as any).profiles
      })) || [];

      setLogs(formattedLogs);
    } catch (error) {
      console.error('Erreur lors de la récupération des logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const logActivity = async (
    action: string,
    entityType: string,
    entityName: string,
    details?: any
  ) => {
    try {
      const { error } = await supabase.rpc('log_activity', {
        p_action: action,
        p_entity_type: entityType,
        p_entity_name: entityName,
        p_details: details || null
      });

      if (error) throw error;
      
      // Actualiser les logs après ajout
      fetchLogs();
    } catch (error) {
      console.error('Erreur lors de l\'ajout du log:', error);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  return {
    logs,
    loading,
    logActivity,
    refetch: fetchLogs
  };
};
